import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, KeyRound, Search, UserPlus } from 'lucide-react';
import { listUsers, createUser, updateUser, resetUserPassword, ROLE_LABELS } from '../lib/accountService';
import { fetchRoster } from '../lib/routingService';

const ROLES = Object.keys(ROLE_LABELS);

const ROLE_HELP = {
  admin: 'Sees everything and manages logins.',
  manager: 'Sees the leads of their zone and their team, can reassign and reopen leads.',
  salesperson: 'Sees only their own leads and updates their status.',
  staff: 'Can look at the leads of their zone, but cannot change anything.',
};

const ROLE_ORDER = { admin: 0, manager: 1, salesperson: 2, staff: 3 };

function errText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

function statusOf(user) {
  if (!user.active) return { key: 'off', label: 'Deactivated' };
  if (user.must_change_password) return { key: 'wait', label: 'Waiting for first sign-in' };
  return { key: 'on', label: 'Active' };
}

function lastSeen(user) {
  if (!user.last_login_at) return 'Never signed in';
  const d = new Date(user.last_login_at);
  if (Number.isNaN(d.getTime())) return '';
  return `Last sign-in ${d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`;
}

export default function UsersTab({ apiUrl, currentUser }) {
  const [users, setUsers] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', role: 'salesperson', person_id: '' });
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await listUsers(apiUrl);
      setUsers(data?.items || []);
    } catch (err) {
      toast.error(errText(err, 'Could not load logins'));
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
    fetchRoster(apiUrl)
      .then((r) => setPeople(Array.isArray(r) ? r : r?.items || r?.people || []))
      .catch(() => setPeople([]));
  }, [apiUrl, load]);

  const peopleById = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);
  const needsPerson = form.role === 'manager' || form.role === 'salesperson';
  const eligible = people.filter((p) => (form.role === 'manager' ? p.role === 'manager' : p.receives_leads));

  const counts = useMemo(() => {
    const c = { all: users.length, on: 0, wait: 0, off: 0 };
    users.forEach((u) => { c[statusOf(u).key] += 1; });
    return c;
  }, [users]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...users]
      .filter((u) => (!roleFilter || u.role === roleFilter)
        && (!statusFilter || statusOf(u).key === statusFilter)
        && (!q || `${u.email} ${u.name || ''}`.toLowerCase().includes(q)))
      .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9)
        || (a.name || a.email).localeCompare(b.name || b.email));
  }, [users, query, roleFilter, statusFilter]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await createUser(apiUrl, { ...form, person_id: needsPerson ? form.person_id : null });
      setSecret({ email: data.user.email, password: data.temporary_password });
      setForm({ email: '', name: '', role: 'salesperson', person_id: '' });
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not create the login'));
    } finally {
      setBusy(false);
    }
  };

  const change = async (user, body, message) => {
    try {
      await updateUser(apiUrl, user.id, body);
      toast.success(message);
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not update the login'));
    }
  };

  const reset = async (user) => {
    if (!window.confirm(`Reset the password for ${user.email}? They will be signed out.`)) return;
    try {
      const data = await resetUserPassword(apiUrl, user.id);
      setSecret({ email: user.email, password: data.temporary_password });
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not reset the password'));
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret.password);
      toast.success('Password copied');
    } catch {
      toast.error('Could not copy. Select the password and copy it by hand.');
    }
  };

  return (
    <div className="users-tab" data-testid="users-tab">
      <div className="section-header">
        <div>
          <h2><KeyRound size={22} /> Team &amp; Access</h2>
          <p className="campaign-detail-sub">
            Each person signs in with their work email. A new or reset login gets a one-time password,
            and they choose their own at first sign-in.
          </p>
        </div>
      </div>

      <div className="users-summary">
        <button type="button" className={`users-chip${!statusFilter ? ' active' : ''}`} onClick={() => setStatusFilter('')}>
          <strong>{counts.all}</strong> All logins
        </button>
        <button type="button" className={`users-chip${statusFilter === 'on' ? ' active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'on' ? '' : 'on')}>
          <strong>{counts.on}</strong> Active
        </button>
        <button type="button" className={`users-chip${statusFilter === 'wait' ? ' active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'wait' ? '' : 'wait')}>
          <strong>{counts.wait}</strong> Not signed in yet
        </button>
        <button type="button" className={`users-chip${statusFilter === 'off' ? ' active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'off' ? '' : 'off')}>
          <strong>{counts.off}</strong> Deactivated
        </button>
      </div>

      {secret ? (
        <div className="users-secret" role="status" data-testid="users-secret">
          <div>
            <p className="users-secret-title">One-time password for <strong>{secret.email}</strong></p>
            <p className="users-secret-note">Shown only now. Send it to them privately (not in a group). It stops working as soon as they choose their own.</p>
            <code data-testid="users-secret-value"> {secret.password} </code>
          </div>
          <div className="users-secret-actions">
            <button type="button" className="campaign-secondary-btn" onClick={copySecret}><Copy size={15} /> Copy</button>
            <button type="button" className="campaign-primary-btn" onClick={() => setSecret(null)}>Done</button>
          </div>
        </div>
      ) : null}

      <form onSubmit={submit} className="users-card users-form" data-testid="users-form">
        <h3><UserPlus size={18} /> Add a login</h3>
        <div className="users-form-grid">
          <label>Work email
            <input type="email" placeholder="name@company.com" value={form.email} required data-testid="user-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label>Name
            <input type="text" placeholder="Full name" value={form.name} data-testid="user-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label>Role
            <select value={form.role} data-testid="user-role" onChange={(e) => setForm({ ...form, role: e.target.value, person_id: '' })}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </label>
          {needsPerson ? (
            <label>Which person in the team list?
              <select value={form.person_id} required data-testid="user-person" onChange={(e) => setForm({ ...form, person_id: e.target.value })}>
                <option value="">Choose a person…</option>
                {eligible.map((p) => <option key={p.id} value={p.id}>{p.name}{p.zone ? ` (${p.zone})` : ''}</option>)}
              </select>
            </label>
          ) : null}
        </div>
        <p className="users-help">{ROLE_HELP[form.role]}{needsPerson ? ' The login is tied to this person, which decides which leads it can see.' : ''}</p>
        <button type="submit" className="campaign-primary-btn" disabled={busy} data-testid="user-create">
          {busy ? 'Creating…' : 'Create login'}
        </button>
      </form>

      <div className="users-card">
        <div className="users-toolbar">
          <label className="users-search">
            <Search size={16} />
            <input type="search" placeholder="Search by name or email" value={query} aria-label="Search logins"
              onChange={(e) => setQuery(e.target.value)} />
          </label>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="Filter by role">
            <option value="">All roles</option>
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <span className="users-count">{shown.length} of {users.length}</span>
        </div>

        <div className="users-list" role="table" aria-label="Logins">
          <div className="users-row users-row-head" role="row">
            <span role="columnheader">Person</span>
            <span role="columnheader">Role</span>
            <span role="columnheader">Linked to</span>
            <span role="columnheader">Status</span>
            <span role="columnheader" className="users-actions-head">Actions</span>
          </div>
          {shown.length === 0 ? <p className="users-empty">No logins match.</p> : null}
          {shown.map((u) => {
            const self = u.email === currentUser?.username;
            const st = statusOf(u);
            const person = peopleById[u.person_id];
            return (
              <div className="users-row" role="row" key={u.id} data-testid={`user-row-${u.email}`}>
                <span className="users-who" role="cell">
                  <strong>{u.name || u.email}{self ? <em className="users-you">You</em> : null}</strong>
                  <small>{u.email}</small>
                </span>
                <span role="cell"><span className={`users-badge role-${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span></span>
                <span role="cell" className="users-linked">
                  {person ? <>{person.name}{person.zone ? <small>{person.zone}</small> : null}</> : '—'}
                </span>
                <span role="cell" className="users-status-cell">
                  <span className={`users-pill ${st.key}`}>{st.label}</span>
                  <small>{st.key === 'off' ? '' : lastSeen(u)}</small>
                </span>
                <span role="cell" className="users-actions">
                  <button type="button" className="campaign-secondary-btn" onClick={() => reset(u)}>Reset password</button>
                  {!self ? (
                    <button type="button" className={u.active ? 'users-danger-btn' : 'campaign-secondary-btn'}
                      onClick={() => change(u, { active: !u.active }, u.active ? 'Login deactivated' : 'Login reactivated')}>
                      {u.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
